<!--
  TeTableBuilderDialogComponent Template
  =======================================
  3-pane dialog for building FreeMarker-driven HTML tables.
  Left:   Attribute picker (grouped by own / join / nested)
  Middle: Selected columns (reorderable, editable headers)
  Right:  Cell template editor with presets and preview
-->

<p-dialog [header]="dialogTitle"
          [(visible)]="visible"
          (visibleChange)="visibleChange.emit($event)"
          (onHide)="handleCancel()"
          [modal]="true"
          [style]="{width: '90vw', maxWidth: '1200px', height: '75vh'}"
          [draggable]="false">

  <div class="te-tbl-builder">

    <!-- ================================================================= -->
    <!-- LEFT PANE: Attribute Picker                                       -->
    <!-- ================================================================= -->
    <div class="te-tbl-picker">
      <div class="te-tbl-picker-header">Verfügbare Attribute</div>

      @if (busy) {
        <div class="te-tbl-picker-loading">
          <i class="fa-regular fa-spinner fa-spin"></i> Lade Metadaten...
        </div>
      }

      <div class="te-tbl-picker-body">
        <p-tree [value]="pickerTreeNodes"
                (onNodeExpand)="handlePickerNodeExpand($event)"
                styleClass="p-fluid text-sm border-0">

          <!-- Custom toggler icon -->
          <ng-template pTemplate="togglericon" let-expanded>
            <span class="te-tree-toggler">{{ expanded ? '▼' : '▶' }}</span>
          </ng-template>

          <!-- Group: own attributes -->
          <ng-template let-node pTemplate="object-root">
            <div class="te-tree-row w-full">
              <span class="te-tree-label">{{ node.label }}</span>
            </div>
          </ng-template>

          <!-- Group: join -->
          <ng-template let-node pTemplate="join">
            <div class="te-tree-row w-full">
              <span class="te-tree-label">{{ node.label }}</span>
            </div>
          </ng-template>

          <!-- Group: nested collection -->
          <ng-template let-node pTemplate="collection">
            <div class="te-tree-row w-full">
              <span class="te-tree-label">{{ node.label }}</span>
            </div>
          </ng-template>

          <!-- Attribute leaf nodes — click to add column -->
          <ng-template let-node pTemplate="attribute">
            <div class="te-tree-row w-full" [class.te-attr-added]="isColumnSelected(node.data.path)">
              <span class="te-tree-label">{{ node.label }}
                <br/><span class="text-xs text-500">{{ node.data.groupRef?.title }}</span></span>
              <span class="te-node-actions">
                <i class="fa-regular fa-clipboard te-copy-btn"
                   pTooltip="Kopieren" tooltipPosition="top"
                   (click)="handleCopyPopover(node.data.attrRef, node.data.groupRef, $event)"></i>
                @if (!isColumnSelected(node.data.path)) {
                  <p-button icon="fa-regular fa-plus" [text]="true"
                            styleClass="p-button-sm te-tbl-add-btn"
                            pTooltip="Als Spalte hinzufügen" tooltipPosition="top"
                            (click)="addColumn(node.data.attrRef, node.data.groupRef); $event.stopPropagation()"></p-button>
                } @else {
                  <i class="fa-regular fa-check te-tbl-pick-added-icon"></i>
                }
              </span>
            </div>
          </ng-template>

        </p-tree>
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- MIDDLE PANE: Selected Columns                                     -->
    <!-- ================================================================= -->
    <div class="te-tbl-selected">
      <div class="te-tbl-selected-header">
        {{ selectedColumns.length }} Spalte{{ selectedColumns.length !== 1 ? 'n' : '' }} ausgewählt
      </div>

      <div class="te-tbl-selected-body">
        @if (selectedColumns.length === 0) {
          <div class="te-tbl-selected-empty">
            <i class="fa-regular fa-table-columns"></i>
            <p>Klicken Sie links auf Attribute um Spalten hinzuzufügen</p>
          </div>
        }

        @for (col of selectedColumns; track col.path; let i = $index) {
          <div class="te-tbl-sel-col"
               [class.active]="activeCellEditorIdx === i"
               [class.custom-template]="hasCustomTemplate(col)"
               draggable="true"
               (dragstart)="dragStart($event, i)"
               (dragover)="dragOver($event)"
               (drop)="dragDrop($event, i)"
               (dragleave)="dragLeave($event)">

            <span class="te-tbl-sel-grip" title="Ziehen zum Verschieben">⠿</span>

            <i [class]="getColumnIcon(col)"></i>

            <span class="te-tbl-sel-info" (click)="selectColumnForEdit(i)" title="Klicken zum Bearbeiten">
              <span class="te-tbl-sel-label">{{ col.label }}</span>
              <span class="text-xs text-500">{{ col.group }}</span>
            </span>

            <input pInputText
                   class="te-tbl-sel-header-input"
                   [(ngModel)]="col.header"
                   placeholder="Spaltenüberschrift"
                   (click)="$event.stopPropagation()"
                   (ngModelChange)="refreshPreview()">

            <span class="te-tbl-sel-edit"
                  [class.active]="activeCellEditorIdx === i"
                  (click)="selectColumnForEdit(i)"
                  title="Zellenvorlage bearbeiten">
              <i class="fa-regular fa-pen-to-square"></i>
            </span>

            <span class="te-tbl-sel-remove"
                  (click)="removeColumn(i); $event.stopPropagation()"
                  title="Spalte entfernen">
              <i class="fa-regular fa-xmark"></i>
            </span>
          </div>
        }
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- RIGHT PANE: Cell Template Editor                                  -->
    <!-- ================================================================= -->
    <div class="te-tbl-cell-editor">

      @if (activeCellEditorIdx < 0 || !activeColumn) {
        <div class="te-tbl-cell-empty">
          <i class="fa-regular fa-pen-ruler"></i>
          <p>Spalte auswählen zum Bearbeiten</p>
        </div>
      }

      @if (activeCellEditorIdx >= 0 && activeColumn) {
        <div class="te-tbl-cell-content">
          <div class="te-tbl-cell-header">
            <strong>{{ activeColumn.label }}</strong>
            <span class="te-tbl-cell-path">{{ activeColumn.path }}</span>
          </div>

          <div class="te-tbl-cell-presets">
            <span class="te-tbl-cell-preset"
                  [class.active]="activeColumn.cellPreset === 'plain'"
                  (click)="applyCellPreset('plain')">
              <i class="fa-regular fa-file-lines"></i> Einfach
            </span>
            <span class="te-tbl-cell-preset"
                  [class.active]="activeColumn.cellPreset === 'ifelse'"
                  (click)="applyCellPreset('ifelse')">
              <i class="fa-regular fa-code-branch"></i> Wenn/Sonst
            </span>

            @if (getColumnDataType(activeColumn) === 'boolean') {
              <span class="te-tbl-cell-preset"
                    [class.active]="activeColumn.cellPreset === 'bool'"
                    (click)="applyCellPreset('bool')">
                <i class="fa-regular fa-check-double"></i> Wahr/Falsch
              </span>
            }

            <span class="te-tbl-cell-preset"
                  [class.active]="activeColumn.cellPreset === 'custom'"
                  (click)="applyCellPreset('custom')">
              <i class="fa-regular fa-code"></i> Benutzerdefiniert
            </span>
          </div>

          <textarea pInputTextarea
                    [(ngModel)]="activeColumn.cellTemplate"
                    rows="6"
                    class="w-full te-tbl-cell-textarea"
                    (ngModelChange)="onCellTemplateChange()"></textarea>

          <div class="te-tbl-cell-preview">
            <div class="te-tbl-cell-preview-header">Vorschau</div>
            <pre class="te-tbl-cell-preview-code">{{ activeColumn.cellTemplate }}</pre>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- =================================================================== -->
  <!-- TABLE PREVIEW                                                       -->
  <!-- =================================================================== -->
  @if (selectedColumns.length > 0) {
    <div class="te-tbl-preview mt-3">
      <div class="te-tbl-preview-header">
        <i class="fa-regular fa-table"></i> Tabellen-Vorschau
      </div>
      <div class="te-tbl-preview-body" [innerHTML]="tablePreviewHtml"></div>
    </div>
  }

  <!-- =================================================================== -->
  <!-- FOOTER                                                              -->
  <!-- =================================================================== -->
  <ng-template pTemplate="footer">
    <p-button label="Abbrechen"
              severity="secondary"
              (onClick)="handleCancel()"></p-button>
    <p-button [label]="editMode ? 'Tabelle aktualisieren' : 'Tabelle einfügen'"
              (onClick)="handleInsert()"
              [disabled]="selectedColumns.length === 0"></p-button>
  </ng-template>

  <!-- Copy popover (shared with te-variables) -->
  <mvs-te-copy-popover #copyPopover></mvs-te-copy-popover>

</p-dialog>
